import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Bus, MapPin, Clock, Users, AlertTriangle,
  QrCode, Package, ChevronRight, RefreshCw, Wifi, WifiOff,
  Radio, CheckCircle, Phone, FileText,
  BellRing, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── GPS TRACKER HOOK ───────────────────────────────────────────────
const useBusGPS = (tripId) => {
  const [tracking, setTracking] = useState(false);
  const [lastPos, setLastPos] = useState(null);
  const [sendCount, setSendCount] = useState(0);
  const [gpsError, setGpsError] = useState(null);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const latestPosRef = useRef(null);
  const wakeLockRef = useRef(null);
  const { token } = useAuth();

  const sendPosition = useCallback(async (pos) => {
    if (!pos || !tripId) return;
    try {
      await axios.post(`${API_URL}/driver/trip/${tripId}/gps`, {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        speed: pos.coords.speed || 0,
        heading: pos.coords.heading || 0,
        accuracy: pos.coords.accuracy || 0,
        device_id: (navigator.userAgent || 'DriverPhone').slice(0, 50)
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSendCount(c => c + 1);
      setLastPos(pos);
      setGpsError(null);
    } catch {
      setGpsError('Telemetry transmission issue');
    }
  }, [tripId, token]);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    if (!tripId) { toast.error('Select an active trip first'); return; }
    setTracking(true);
    setGpsError(null);
    if ('wakeLock' in navigator) {
      navigator.wakeLock.request('screen').then(wl => { wakeLockRef.current = wl; }).catch(() => {});
    }
    navigator.geolocation.getCurrentPosition(
      pos => { latestPosRef.current = pos; sendPosition(pos); },
      err => setGpsError(`GPS error: ${err.message}`),
      { enableHighAccuracy: true, timeout: 15000 }
    );
    watchIdRef.current = navigator.geolocation.watchPosition(
      pos => { latestPosRef.current = pos; setLastPos(pos); },
      err => setGpsError(`GPS signal: ${err.message}`),
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 10000 }
    );
    intervalRef.current = setInterval(() => {
      if (latestPosRef.current) sendPosition(latestPosRef.current);
    }, 15000);
    toast.success('📡 Live GPS tracking active!');
  }, [tripId, sendPosition]);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (wakeLockRef.current) { try { wakeLockRef.current.release(); } catch (e) {} wakeLockRef.current = null; }
    setTracking(false);
    toast.info('GPS tracking stopped');
  }, []);

  useEffect(() => () => stopTracking(), [stopTracking]);
  return { tracking, lastPos, sendCount, gpsError, startTracking, stopTracking };
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────
const DriverDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isHost = user?.role === 'conductor';

  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [dropoffs, setDropoffs] = useState([]);
  const [dropoffsLoading, setDropoffsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('trips');
  const [incidentForm, setIncidentForm] = useState({ type: '', description: '' });
  const [submittingIncident, setSubmittingIncident] = useState(false);
  const [expandedPassenger, setExpandedPassenger] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [notifyingDropoff, setNotifyingDropoff] = useState(null);
  const [offboardAlerts, setOffboardAlerts] = useState([]);
  const [collectingRef, setCollectingRef] = useState(null);

  const currentTrip = trips.find(t => t.id === selectedTrip)
    || trips.find(t => t.status === 'in_transit')
    || trips[0];

  const { tracking, lastPos, sendCount, gpsError, startTracking, stopTracking } = useBusGPS(currentTrip?.id);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'driver' && user.role !== 'conductor') {
      toast.error('This portal is only available to drivers and hosts.');
      navigate('/dashboard');
      return;
    }
    fetchTrips();
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentTrip?.status === 'in_transit' && !tracking && navigator.geolocation) {
      startTracking();
    }
  }, [currentTrip?.status, tracking, startTracking]);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/driver/my-trips`, { headers: { Authorization: `Bearer ${token}` } });
      setTrips(res.data || []);
    } catch {
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const fetchManifest = async (tripId) => {
    setManifestLoading(true);
    try {
      const res = await axios.get(`${API_URL}/driver/trip/${tripId}/manifest`, { headers: { Authorization: `Bearer ${token}` } });
      setManifest(res.data);
    } catch {
      toast.error('Failed to load manifest');
    } finally {
      setManifestLoading(false);
    }
  };

  const fetchDropoffs = async () => {
    setDropoffsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/dashboard/dropoff-luggage`, { headers: { Authorization: `Bearer ${token}` } });
      const all = res.data?.dropoff_points || [];
      setDropoffs(currentTrip ? all.filter(d => d.trip_id === currentTrip.id) : all);
    } catch {
      toast.error('Failed to load drop-offs');
    } finally {
      setDropoffsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'luggage') fetchDropoffs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentTrip?.id]);

  const fetchOffboardAlerts = useCallback(async () => {
    if (!currentTrip?.id) return;
    try {
      const res = await axios.get(`${API_URL}/driver/trip/${currentTrip.id}/offboarding-alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffboardAlerts(res.data?.alerts || []);
    } catch {
      // non-blocking
    }
  }, [currentTrip?.id, token]);

  useEffect(() => {
    fetchOffboardAlerts();
    const interval = setInterval(fetchOffboardAlerts, 10000);
    return () => clearInterval(interval);
  }, [fetchOffboardAlerts]);

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`${API_URL}/driver/alerts/${alertId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Alert acknowledged ✓');
      fetchOffboardAlerts();
    } catch {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleCollectBalance = async (bookingRef) => {
    if (!currentTrip?.id) return;
    setCollectingRef(bookingRef);
    try {
      const res = await axios.post(`${API_URL}/driver/trip/${currentTrip.id}/collect-balance/${bookingRef}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data?.message || 'Balance collected successfully!');
      fetchOffboardAlerts();
      if (selectedTrip) fetchManifest(selectedTrip);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record balance collection');
    } finally {
      setCollectingRef(null);
    }
  };

  const handleBoardPassenger = async (bookingRef) => {
    try {
      await axios.post(`${API_URL}/bookings/${bookingRef}/board`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Passenger boarded ✓');
      if (selectedTrip) fetchManifest(selectedTrip);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to board passenger');
    }
  };

  const handleScanSubmit = async () => {
    if (!scanInput.trim()) return;
    const ref = (() => { try { return JSON.parse(scanInput)?.ref || scanInput.trim(); } catch { return scanInput.trim(); } })();
    try {
      await axios.post(`${API_URL}/bookings/${ref}/board`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`✓ Boarded: ${ref}`);
      setScanInput('');
      if (selectedTrip) fetchManifest(selectedTrip);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    }
  };

  const handleIncidentSubmit = async () => {
    if (!incidentForm.type || !incidentForm.description) { toast.error('Fill in all required fields'); return; }
    if (!currentTrip) { toast.error('No active trip selected'); return; }
    setSubmittingIncident(true);
    try {
      const fd = new FormData();
      fd.append('incident_type', incidentForm.type);
      fd.append('description', incidentForm.description);
      await axios.post(`${API_URL}/driver/trip/${currentTrip.id}/incident`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Incident reported to operations');
      setIncidentForm({ type: '', description: '' });
    } catch {
      toast.error('Failed to report incident');
    } finally {
      setSubmittingIncident(false);
    }
  };

  const handleDropoffNotify = async (tripId, dropoffPoint) => {
    setNotifyingDropoff(dropoffPoint);
    try {
      const res = await axios.post(
        `${API_URL}/trips/${tripId}/dropoff-notify`,
        { dropoff_point: dropoffPoint },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || `Notifications sent for ${dropoffPoint}`);
      fetchDropoffs();
    } catch {
      toast.error('Failed to send notifications');
    } finally {
      setNotifyingDropoff(null);
    }
  };

  const statusColor = s => ({
    scheduled: 'bg-blue-100 text-blue-800',
    in_transit: 'bg-green-100 text-green-800',
    completed: 'bg-slate-100 text-slate-600',
    cancelled: 'bg-red-100 text-red-800',
  }[s] || 'bg-slate-100 text-slate-600');

  const proximityBorderColor = s => ({
    approaching: 'border-l-red-500',
    at_station: 'border-l-orange-500',
    en_route: 'border-l-blue-400',
  }[s] || 'border-l-slate-300');

  if (loading) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-panashe-blue" />
    </div>
  );

  const TABS = [
    { id: 'trips', Icon: Bus, label: 'Trips' },
    { id: 'manifest', Icon: Users, label: 'Manifest', disabled: !currentTrip },
    { id: 'luggage', Icon: Package, label: 'Drop-offs' },
    { id: 'scan', Icon: QrCode, label: 'Scan' },
    { id: 'incident', Icon: AlertTriangle, label: 'Incident' },
  ];

  return (
    <div className="min-h-screen bg-slate-100 pb-8">
      {/* ── Header ── */}
      <div className="bg-panashe-blue text-white p-4 sticky top-0 z-20 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-bold">
              {isHost ? '🎟️ Host Portal' : '🚌 Driver Portal'}
            </h1>
            <p className="text-sm text-white/70">{user?.full_name}</p>
          </div>
          <div className="flex items-center gap-3">
            {isOnline
              ? <Wifi className="h-5 w-5 text-green-400" />
              : <WifiOff className="h-5 w-5 text-red-300" />}
            <button onClick={fetchTrips} className="text-white/80 hover:text-white">
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* GPS bar */}
        {currentTrip && (
          <div className={`mt-3 px-3 py-2 rounded-lg flex items-center justify-between text-xs ${tracking ? 'bg-green-900/40' : 'bg-white/10'}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Radio className={`h-4 w-4 ${tracking ? 'text-green-400 animate-pulse' : 'text-white/50'}`} />
              <span>{tracking ? `GPS LIVE · ${sendCount} tx` : 'GPS Standby'}</span>
              {lastPos && (
                <span className="text-white/50 text-[10px]">
                  {lastPos.coords.latitude.toFixed(4)}, {lastPos.coords.longitude.toFixed(4)}
                  {lastPos.coords.speed ? ` · ${(lastPos.coords.speed * 3.6).toFixed(0)} km/h` : ''}
                </span>
              )}
            </div>
            <button
              onClick={tracking ? stopTracking : startTracking}
              className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${tracking ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}
            >
              {tracking ? 'Stop' : 'Start GPS'}
            </button>
          </div>
        )}
        {gpsError && <p className="text-xs text-amber-300 mt-1">⚠ {gpsError}</p>}

        {/* ── LIVE PASSENGER OFFBOARDING NOTIFICATIONS (Host Moving Device) ── */}
        {offboardAlerts.filter(a => a.status === 'unread').length > 0 && (
          <div className="mt-3 space-y-2">
            {offboardAlerts.filter(a => a.status === 'unread').map(alert => (
              <div
                key={alert.id}
                className="bg-amber-400 text-slate-950 p-3 rounded-lg shadow-lg border-2 border-amber-200"
                data-testid={`offboard-alert-${alert.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2.5">
                    <BellRing className="h-5 w-5 text-slate-950 flex-shrink-0 mt-0.5 animate-bounce" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="bg-red-700 text-white text-[10px] font-bold px-1.5 py-0.2 rounded uppercase">
                          Offboarding: {alert.dropoff_point}
                        </span>
                        {alert.eta_minutes && (
                          <span className="text-[11px] font-bold text-slate-800">
                            (~{alert.eta_minutes} min away)
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-bold mt-1 text-slate-950 leading-snug">
                        {alert.message}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    {alert.pending_balance > 0 && (
                      <Button
                        size="sm"
                        onClick={() => handleCollectBalance(alert.booking_ref)}
                        disabled={collectingRef === alert.booking_ref}
                        className="bg-green-700 hover:bg-green-800 text-white font-bold text-xs h-7 px-2.5 shadow"
                      >
                        {collectingRef === alert.booking_ref ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                        💰 Collect {alert.currency === 'ZAR' ? 'R' : '$'}{Number(alert.pending_balance).toFixed(2)}
                      </Button>
                    )}
                    <button
                      onClick={() => handleAcknowledgeAlert(alert.id)}
                      className="bg-slate-900/80 hover:bg-slate-950 text-white text-[11px] font-semibold px-2 py-0.5 rounded text-center"
                    >
                      ✓ Dismiss
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Tab Bar ── */}
      <div className="flex border-b bg-white sticky top-[72px] z-10 shadow-sm">
        {TABS.map(({ id, Icon, label, disabled }) => (
          <button
            key={id}
            disabled={!!disabled}
            onClick={() => {
              setActiveTab(id);
              if (id === 'manifest' && currentTrip) fetchManifest(currentTrip.id);
            }}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors
              ${activeTab === id ? 'text-panashe-blue border-b-2 border-panashe-blue' : 'text-slate-500'}
              ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      <div className="p-4 space-y-4 max-w-md mx-auto">

        {/* ═══════════ TRIPS TAB ═══════════ */}
        {activeTab === 'trips' && (
          <div className="space-y-4">
            {currentTrip && (
              <Card className="border-2 border-panashe-blue shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={statusColor(currentTrip.status)}>
                      {currentTrip.status?.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <span className="font-mono text-xs text-slate-400">{currentTrip.trip_number}</span>
                  </div>
                  <p className="font-heading font-bold text-lg">{currentTrip.route_name}</p>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs text-slate-600">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-panashe-blue" />
                      {currentTrip.departure_datetime && format(parseISO(currentTrip.departure_datetime), 'HH:mm dd/MM')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Bus className="h-3.5 w-3.5 text-panashe-blue" />
                      {currentTrip.bus_registration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5 text-panashe-blue" />
                      {currentTrip.passenger_count || 0} pax
                    </span>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1"
                      onClick={() => { setSelectedTrip(currentTrip.id); setActiveTab('manifest'); fetchManifest(currentTrip.id); }}>
                      <FileText className="h-4 w-4 mr-1" /> Manifest
                    </Button>
                    {currentTrip.status === 'scheduled' ? (
                      <Button size="sm" variant="outline" className="flex-1" disabled>
                        <Clock className="h-4 w-4 mr-1" /> Starts at departure
                      </Button>
                    ) : currentTrip.status === 'in_transit' ? (
                      <Button size="sm" variant="outline" className="flex-1" disabled>
                        <Clock className="h-4 w-4 mr-1" /> Completes after 24h
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            )}

            <p className="font-heading font-bold text-slate-700 text-sm">All My Trips</p>
            {trips.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-slate-500">
                <Bus className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p>No trips assigned yet</p>
              </CardContent></Card>
            ) : trips.map(trip => (
              <Card
                key={trip.id}
                className={`cursor-pointer transition-all ${selectedTrip === trip.id ? 'ring-2 ring-panashe-blue' : ''}`}
                onClick={() => { setSelectedTrip(trip.id); fetchManifest(trip.id); setActiveTab('manifest'); }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={statusColor(trip.status)} style={{ fontSize: '10px' }}>
                          {trip.status?.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <span className="text-xs text-slate-400 font-mono">{trip.trip_number}</span>
                      </div>
                      <p className="font-medium">{trip.route_name}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-0.5">
                          <Clock className="h-3 w-3" />
                          {trip.departure_datetime && format(parseISO(trip.departure_datetime), 'HH:mm dd/MM')}
                        </span>
                        <span className="flex items-center gap-0.5">
                          <Users className="h-3 w-3" />
                          {trip.passenger_count} pax
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-300 mt-1" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ═══════════ MANIFEST TAB ═══════════ */}
        {activeTab === 'manifest' && (
          <div className="space-y-3">
            {manifestLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-panashe-blue" /></div>
            ) : manifest ? (
              <>
                {/* Summary row */}
                <Card>
                  <CardContent className="p-3 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-2xl font-bold">{manifest.total_passengers}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{manifest.boarded_count || 0}</p>
                      <p className="text-xs text-slate-500">Boarded</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">
                        {manifest.total_passengers - (manifest.boarded_count || 0)}
                      </p>
                      <p className="text-xs text-slate-500">Pending</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Passengers */}
                {manifest.passengers?.map((p, i) => (
                  <Card key={i} className={`border ${p.boarded ? 'border-green-200 bg-green-50' : p.checked_in ? 'border-amber-200 bg-amber-50' : ''}`}>
                    <CardContent className="p-3">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setExpandedPassenger(expandedPassenger === i ? null : i)}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${p.boarded ? 'bg-green-600' : p.checked_in ? 'bg-amber-500' : 'bg-slate-400'}`}>
                            {p.seat}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{p.name}</p>
                            <p className="text-xs text-slate-400 font-mono">{p.booking_ref}</p>
                            {p.dropoff_point && (
                              <p className="text-[11px] text-indigo-700 font-semibold mt-0.5">
                                📍 Drop-off: {p.dropoff_point}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {p.balance_due > 0 && (
                            <Badge variant="destructive" className="text-[10px] bg-red-600 text-white font-bold">
                              Owes {p.currency_symbol || '$'}{p.balance_due.toFixed(2)}
                            </Badge>
                          )}
                          {p.boarded
                            ? <CheckCircle className="h-4 w-4 text-green-600" />
                            : expandedPassenger === i
                              ? <ChevronUp className="h-4 w-4 text-slate-400" />
                              : <ChevronDown className="h-4 w-4 text-slate-400" />}
                        </div>
                      </div>
                      {expandedPassenger === i && !p.boarded && (
                        <div className="mt-3 pt-3 border-t space-y-2">
                          {p.phone && (
                            <a href={`tel:${p.phone}`} className="flex items-center gap-1 text-xs text-blue-600">
                              <Phone className="h-3.5 w-3.5" /> {p.phone}
                            </a>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-xs h-8"
                              onClick={() => handleBoardPassenger(p.booking_ref)}>
                              <CheckCircle className="h-3.5 w-3.5 mr-1" /> Mark Boarded
                            </Button>
                            {p.balance_due > 0 && (
                              <Button
                                size="sm"
                                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs h-8 shadow"
                                onClick={() => handleCollectBalance(p.booking_ref)}
                                disabled={collectingRef === p.booking_ref}
                              >
                                {collectingRef === p.booking_ref ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                💰 Collect {p.currency_symbol || '$'}{p.balance_due.toFixed(2)}
                              </Button>
                            )}
                            {p.phone && (
                              <a href={`tel:${p.phone}`}
                                className="flex-1 flex items-center justify-center gap-1 text-xs h-8 border rounded-md border-slate-300 text-slate-700">
                                <Phone className="h-3.5 w-3.5" /> Call
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {/* Luggage summary */}
                {manifest.luggage_count > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="h-4 w-4 text-purple-600" />
                        On-Board Luggage ({manifest.luggage_count})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-2">
                      {manifest.luggage?.map((l, i) => (
                        <div key={i} className="p-2 bg-purple-50 border border-purple-200 rounded text-xs">
                          <div className="flex justify-between">
                            <span className="font-mono font-bold text-purple-700">{l.ref}</span>
                            <span className="text-slate-500">{l.weight}kg</span>
                          </div>
                          <p className="text-slate-600">{l.description}</p>
                          <p className="text-slate-500">From: {l.sender} → {l.receiver}</p>
                          {l.dropoff_point && (
                            <p className="text-orange-600 font-medium mt-0.5">📍 Drop-off: {l.dropoff_point}</p>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card><CardContent className="py-10 text-center text-slate-500">
                <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p>Select a trip to view manifest</p>
              </CardContent></Card>
            )}
          </div>
        )}

        {/* ═══════════ LUGGAGE DROP-OFFS TAB ═══════════ */}
        {activeTab === 'luggage' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-slate-700 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-orange-500" /> Drop-off Points
              </h2>
              <Button size="sm" variant="outline" onClick={fetchDropoffs} disabled={dropoffsLoading}>
                {dropoffsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>

            {dropoffsLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-panashe-blue" /></div>
            ) : dropoffs.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-slate-500">
                <Package className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                <p>No luggage drop-offs for this trip</p>
              </CardContent></Card>
            ) : dropoffs.map((d, i) => (
              <Card key={i} className={`border-l-4 ${proximityBorderColor(d.proximity_status)}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-orange-500" />{d.dropoff_point}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <Badge className={`text-[10px] ${
                          d.proximity_status === 'approaching' ? 'bg-red-100 text-red-700' :
                          d.proximity_status === 'at_station' ? 'bg-orange-100 text-orange-700' :
                          'bg-blue-100 text-blue-700'}`}>
                          {d.proximity_status === 'approaching' ? '⚠ APPROACHING' :
                           d.proximity_status === 'at_station' ? '📍 AT STOP' :
                           d.proximity_status === 'en_route' ? '🚌 En Route' : '📡 No GPS'}
                        </Badge>
                        {d.eta_minutes && (
                          <span className="text-xs text-slate-500">~{d.eta_minutes} min · {d.distance_km}km</span>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs whitespace-nowrap">
                      {d.total_parcels || d.luggage?.length || 0} parcel(s)
                    </Badge>
                  </div>

                  <div className="space-y-1 mt-2">
                    {d.luggage?.map((l, li) => (
                      <div key={li} className="flex items-center justify-between text-xs bg-slate-50 p-2 rounded border">
                        <div>
                          <span className="font-mono text-purple-700 font-bold">{l.luggage_ref}</span>
                          <span className="text-slate-500 ml-1">{l.description}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-600">{l.receiver_name}</p>
                          {l.receiver_phone && (
                            <a href={`tel:${l.receiver_phone}`} className="text-blue-600 flex items-center gap-0.5 justify-end">
                              <Phone className="h-3 w-3" />{l.receiver_phone}
                            </a>
                          )}
                          {l.receiver_notified && <span className="text-green-600 font-medium">✓ Notified</span>}
                        </div>
                      </div>
                    ))}
                    {d.passengers?.map((p, pi) => (
                      <div key={pi} className="flex items-center justify-between text-xs bg-blue-50 p-2 rounded border border-blue-200">
                        <span className="font-medium">{p.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">Seat {p.seat}</span>
                          {p.phone && <a href={`tel:${p.phone}`} className="text-blue-600"><Phone className="h-3 w-3" /></a>}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    size="sm"
                    className="w-full mt-3 bg-orange-600 hover:bg-orange-700 text-white text-xs"
                    onClick={() => handleDropoffNotify(d.trip_id, d.dropoff_point)}
                    disabled={notifyingDropoff === d.dropoff_point}
                  >
                    {notifyingDropoff === d.dropoff_point
                      ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Sending...</>
                      : <><BellRing className="h-3.5 w-3.5 mr-1" /> Notify Recipients</>}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ═══════════ SCAN TAB ═══════════ */}
        {activeTab === 'scan' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <QrCode className="h-5 w-5 text-panashe-blue" /> Quick Board Passenger
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-500">Enter or scan a booking reference to mark as boarded.</p>
                <Input
                  placeholder="Booking ref or QR scan..."
                  value={scanInput}
                  onChange={e => setScanInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleScanSubmit()}
                  className="font-mono"
                  autoFocus
                />
                <Button className="w-full btn-panashe" onClick={handleScanSubmit} disabled={!scanInput.trim()}>
                  <CheckCircle className="h-4 w-4 mr-2" /> Mark as Boarded
                </Button>
              </CardContent>
            </Card>

            {manifest?.passengers?.filter(p => !p.boarded).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Pending Passengers</CardTitle>
                </CardHeader>
                <CardContent className="max-h-80 overflow-y-auto space-y-2">
                  {manifest.passengers.filter(p => !p.boarded).map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded border">
                      <div>
                        <span className="font-bold text-sm mr-2">#{p.seat}</span>
                        <span className="text-sm">{p.name}</span>
                        <p className="text-xs text-slate-400 font-mono">{p.booking_ref}</p>
                      </div>
                      <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700"
                        onClick={() => handleBoardPassenger(p.booking_ref)}>
                        Board
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            {manifest?.passengers?.every(p => p.boarded) && manifest.passengers.length > 0 && (
              <Card><CardContent className="py-6 text-center text-green-600 font-medium">
                <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                All passengers boarded!
              </CardContent></Card>
            )}
          </div>
        )}

        {/* ═══════════ INCIDENT TAB ═══════════ */}
        {activeTab === 'incident' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <AlertTriangle className="h-5 w-5" /> Report Incident
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!currentTrip && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                    ⚠ Go to Trips tab and select an active trip first.
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium block mb-1">Incident Type *</label>
                  <select
                    className="w-full border border-slate-300 rounded-md p-2 text-sm bg-white"
                    value={incidentForm.type}
                    onChange={e => setIncidentForm(f => ({ ...f, type: e.target.value }))}
                  >
                    <option value="">Select type...</option>
                    <option value="breakdown">Mechanical Breakdown</option>
                    <option value="accident">Road Accident</option>
                    <option value="passenger_emergency">Passenger Emergency</option>
                    <option value="border_delay">Border / Customs Delay</option>
                    <option value="route_deviation">Route Deviation</option>
                    <option value="weather">Severe Weather</option>
                    <option value="security">Security Incident</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Description *</label>
                  <textarea
                    rows={4}
                    className="w-full border border-slate-300 rounded-md p-2 text-sm"
                    placeholder="Describe the incident in detail..."
                    value={incidentForm.description}
                    onChange={e => setIncidentForm(f => ({ ...f, description: e.target.value }))}
                  />
                </div>
                {lastPos && (
                  <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded border">
                    📍 GPS at report time: {lastPos.coords.latitude.toFixed(5)}, {lastPos.coords.longitude.toFixed(5)}
                  </div>
                )}
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={handleIncidentSubmit}
                  disabled={submittingIncident || !currentTrip}
                >
                  {submittingIncident
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reporting...</>
                    : <><AlertTriangle className="h-4 w-4 mr-2" /> Report to Operations</>}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <p className="text-sm font-bold text-slate-700 mb-3">📞 Emergency Contacts</p>
                <div className="space-y-2">
                  {[
                    { label: 'Operations (Harare)', number: '+263786563430' },
                    { label: 'Durban Office', number: '+27656139181' },
                    { label: 'PMB Office', number: '+27843797126' },
                    { label: 'Mooi River', number: '+27622971043' },
                  ].map((c, i) => (
                    <a key={i} href={`tel:${c.number}`}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded border hover:bg-slate-100 transition-colors">
                      <span className="text-sm text-slate-700">{c.label}</span>
                      <span className="text-sm text-blue-600 font-mono flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5" />{c.number}
                      </span>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverDashboard;
